import React, { useEffect, useRef, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { getAuthorizationToken } from '../../../../../lib/access.es6'
import { getEnvironmentInformation } from '../../../../../lib/utils.es6'
import { getUssdOrgUuid } from '../../../../../lib/ussdSessionsRequests.es6'
import { loadVendorBundle } from '../vendorBundle'

function ussdSessionsBaseUrl() {
  const env = getEnvironmentInformation()
  return env ? env.servicesURLs.ussdSessions : 'http://localhost:8090/'
}

// Read-only embed of RapidPro's real flow editor canvas, so a business user can see a flow's actual
// configured screens/messages the same way a developer would in RapidPro itself - not a re-derived
// summary. `viewonly` is load-bearing here, not cosmetic: without it the widget auto-saves any change
// (drag, delete, etc.) 2 seconds after the fact with no confirmation, straight to the real flow revision
// a customer is actively using. See Editor.ts's `viewOnly` property doc comment for the full rationale.
export default function FlowViewer({ flowUuid }) {
  const ref = useRef(null)
  const [ready, setReady] = useState(false)
  const [orgUuid, setOrgUuid] = useState(null)

  useEffect(() => {
    let cancelled = false
    loadVendorBundle().then(() => {
      if (!cancelled) setReady(true)
    })
    // fixedrevisionsendpoint has to be present as a JSX attribute at mount time - Editor.ts's own
    // firstUpdated() only fires its initial revision fetch once, so resolving the org uuid after the
    // element has already rendered would be too late.
    getUssdOrgUuid().then(org => {
      if (!cancelled) setOrgUuid(org)
    })
    return () => {
      cancelled = true
    }
  }, [])

  if (!flowUuid) {
    return <Typography variant="body2">Choose a flow to view.</Typography>
  }

  if (!ready || !orgUuid) {
    return <Typography variant="body2">Loading flow editor…</Typography>
  }

  return (
    <Box
      sx={{ height: 'calc(100vh - 64px)', width: '100%', position: 'relative' }}
    >
      {/* see Simulator.js for why a bare <temba-store> is required - Editor.ts also calls getStore().
          No completion/fields/groups endpoints are set: those only matter for full node-editing dialogs
          (out of scope for a view-only canvas), and each is independently gated behind its own endpoint
          attribute being present, so omitting them is safe. */}
      {/* eslint-disable-next-line react/no-unknown-property */}
      <temba-store />
      {/* temba-flow-editor defaults to display:inline with no set size (it's a plain custom element) -
          its internal #editor-container/#editor rely on flex layout and an actual box to scroll within
          (see the component's own demo harness, temba-components-aspin/demo/components/flow/example.html,
          which sets this exact CSS on the host page). Without it the canvas collapses to zero size, which
          also starves jsPlumb of real node positions to draw connector lines between - not just a missing
          scrollbar. */}
      {/* eslint-disable-next-line react/no-unknown-property */}
      <temba-flow-editor
        ref={ref}
        flow={flowUuid}
        fixedrevisionsendpoint={`${ussdSessionsBaseUrl()}mr/business/flows/${orgUuid}/${flowUuid}/revisions`}
        authtoken={getAuthorizationToken()}
        viewonly=""
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%'
        }}
      />
    </Box>
  )
}
